import React, { useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  TableSortLabel,
  Tooltip,
  Skeleton,
  Button,
} from "@mui/material";
// No framer-motion needed here anymore if we don't animate row switches
// import { AnimatePresence } from "framer-motion";
import { FlowTask } from "../../interfaces/flowTask.interface";
import { ListViewRow } from "./ListViewRow";
import AddIcon from "@mui/icons-material/Add";

interface ListViewProps {
  tasks: FlowTask[];
  isLoading?: boolean;
  onAddTask: () => void;
  isPreviewMode?: boolean;
  isLimitReached?: boolean;
}

type SortField = "status" | "title" | "priority" | "deadline";
type SortOrder = "asc" | "desc";

export const ListView = ({ tasks, isLoading, onAddTask, isPreviewMode = false, isLimitReached = false }: ListViewProps) => {
  const [sortField, setSortField] = React.useState<SortField>("status");
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedTasks = useMemo(() => {
    const sorted = [...tasks];
    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "status":
          comparison = a.status.localeCompare(b.status);
          break;
        case "title":
          comparison = a.name.localeCompare(b.name);
          break;
        case "priority":
          const priorityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
          comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
          break;
        case "deadline":
          const dateA = a.deadline ? new Date(a.deadline).getTime() : 0;
          const dateB = b.deadline ? new Date(b.deadline).getTime() : 0;
          comparison = dateA - dateB;
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [tasks, sortField, sortOrder]);

  if (isLoading) {
    return (
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "none", border: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 600 }}>Title</TableCell>
              <TableCell sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 600 }}>Priority</TableCell>
              <TableCell sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 600 }}>Deadline</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4, 5].map((i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width="60%" />
                </TableCell>
                <TableCell>
                  <Skeleton variant="rectangular" width={70} height={20} sx={{ borderRadius: 1 }} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={100} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (tasks.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: "var(--fx-surface-texttertiary, #94A3B8)", width: "100%" }}>
        <Typography variant="body1">No tasks found</Typography>
        {!isPreviewMode && (
          <Tooltip title={isLimitReached ? "Task limit reached (25)" : ""} arrow>
            <span>
              <Button
                variant="contained"
                disabled={isLimitReached}
                sx={{
                  mt: 2,
                  border: "1px solid transparent",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  color: isLimitReached ? "#d1d5db" : "var(--fx-surface-textsecondary, #64748B)",
                  borderRadius: 1,
                  width: "fit-content",
                  height: "40px",
                  display: "flex",
                  justifyContent: "center",
                  justifySelf: "center",
                  alignItems: "center",
                  alignSelf: "center",
                  cursor: isLimitReached ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: isLimitReached ? "transparent" : "#f9fafb",
                    color: isLimitReached ? "#d1d5db" : "var(--fx-surface-textsecondary, #64748B)",
                    boxShadow: "none",
                  },
                }}
                onClick={() => onAddTask()}
              >
                <AddIcon />
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      <TableContainer
        component={Paper}
        sx={{ borderRadius: 2, overflow: "hidden", maxHeight: "calc(100vh - 300px)", boxShadow: "none", border: "1px solid var(--fx-surface-border, #E2E8F0)" }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)", bgcolor: "var(--fx-surface-sunken, #F8FAFC)", borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
                <TableSortLabel
                  active={sortField === "status"}
                  direction={sortField === "status" ? sortOrder : "asc"}
                  onClick={() => handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)", bgcolor: "var(--fx-surface-sunken, #F8FAFC)", borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)", width: "40%" }}>
                <TableSortLabel
                  active={sortField === "title"}
                  direction={sortField === "title" ? sortOrder : "asc"}
                  onClick={() => handleSort("title")}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)", bgcolor: "var(--fx-surface-sunken, #F8FAFC)", borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
                <TableSortLabel
                  active={sortField === "priority"}
                  direction={sortField === "priority" ? sortOrder : "asc"}
                  onClick={() => handleSort("priority")}
                >
                  Priority
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)", bgcolor: "var(--fx-surface-sunken, #F8FAFC)", borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
                <TableSortLabel
                  active={sortField === "deadline"}
                  direction={sortField === "deadline" ? sortOrder : "asc"}
                  onClick={() => handleSort("deadline")}
                >
                  Deadline
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTasks.map((task) => (
              <ListViewRow key={task.uid} task={task} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!isPreviewMode && (
        <Tooltip title={isLimitReached ? "Task limit reached (25)" : ""} arrow>
          <span>
            <Button
              variant="contained"
              color="primary"
              disabled={isLimitReached}
              sx={{
                mt: 2,
                border: "1px solid transparent",
                backgroundColor: "transparent",
                boxShadow: "none",
                color: isLimitReached ? "#d1d5db" : "var(--fx-surface-textsecondary, #64748B)",
                borderRadius: 1,
                width: "fit-content",
                height: "40px",
                display: "flex",
                justifyContent: "center",
                justifySelf: "center",
                alignItems: "center",
                alignSelf: "center",
                cursor: isLimitReached ? "not-allowed" : "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: isLimitReached ? "transparent" : "#f9fafb",
                  color: isLimitReached ? "#d1d5db" : "var(--fx-surface-textsecondary, #64748B)",
                  boxShadow: "none",
                },
              }}
              onClick={() => onAddTask()}
            >
              <AddIcon />
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};
